import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const ChallengeHeader = ({ challenge, onEditChallenge }) => {
  const progressPercentage = (challenge?.completedProofs / challenge?.totalProofs) * 100;

  const getGoalTypeLabel = (type) => {
    const labels = {
      fixed: '고정 목표',
      split: '분할 목표',
      thirty_day: '30일 챌린지'
    };
    return labels?.[type] || type;
  };

  const getGoalTypeIcon = (type) => {
    const icons = {
      fixed: 'Target',
      split: 'Layers',
      thirty_day: 'Calendar'
    };
    return icons?.[type] || 'Target';
  };

  return (
    <div className="bg-card rounded-lg shadow-gaming-md p-4 md:p-6 lg:p-8 mb-6 md:mb-8">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 md:gap-6 mb-5 md:mb-6">
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-3 mb-3">
            <div className="flex-shrink-0 w-12 h-12 md:w-14 md:h-14 bg-muted rounded-md overflow-hidden border border-border">
              {challenge?.coverImage ? (
                <Image
                  src={challenge.coverImage}
                  alt={challenge?.coverImageAlt || challenge?.title || 'Challenge cover image'}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-primary/10" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl md:text-2xl lg:text-3xl font-heading font-bold text-foreground truncate">
                {challenge?.title}
              </h1>
              <p className="text-xs md:text-sm text-muted-foreground caption mt-1">
                {getGoalTypeLabel(challenge?.goalType)}
              </p>
            </div>
          </div>

          {challenge?.description && (
            <p className="text-sm md:text-base text-muted-foreground line-clamp-2 mb-4">
              {challenge?.description}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-3 md:gap-4">
            <div className="flex items-center space-x-2">
              <Icon name="Calendar" size={16} className="text-muted-foreground" />
              <span className="text-xs md:text-sm text-muted-foreground caption">
                시작일: {challenge?.startDate}
              </span>
            </div>
            {challenge?.endDate && (
              <div className="flex items-center space-x-2">
                <Icon name="CalendarCheck" size={16} className="text-muted-foreground" />
                <span className="text-xs md:text-sm text-muted-foreground caption">
                  종료일: {challenge?.endDate}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col items-start lg:items-end space-y-3 w-full lg:w-auto">
          {onEditChallenge && (
            <button
              onClick={onEditChallenge}
              className="flex items-center space-x-2 px-4 md:px-6 py-2 md:py-3 bg-muted hover:bg-muted/80 text-foreground rounded-md transition-gaming self-start lg:self-auto"
            >
              <Icon name="Edit" size={18} />
              <span className="text-sm md:text-base font-medium">챌린지 편집</span>
            </button>
          )}
          <div className="flex items-center justify-between w-full lg:w-auto gap-3 px-4 py-2 bg-primary/10 rounded-md border border-primary/20">
            <div className="flex items-center gap-2">
              <Icon name="Trophy" size={20} color="var(--color-primary)" />
              <div className="flex items-center space-x-2 px-3 py-1 bg-primary/20 text-primary rounded-full border border-primary/30">
                <span className="text-sm font-medium caption">{challenge?.averageRating?.toFixed(1)}</span>
                <Icon name="Star" size={16} color="currentColor" className="fill-current" />
              </div>
            </div>

            <div className="flex items-center space-x-1">
              {[...Array(5)]?.map((_, index) => (
                <Icon
                  key={index}
                  name="Star"
                  size={18}
                  color="currentColor"
                  className={index < challenge?.averageRating ? 'fill-current text-primary' : 'text-muted-foreground'}
                />
              ))}
              <span className="text-sm text-muted-foreground caption ml-2">
                평균 {challenge?.averageRating?.toFixed(1)}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm md:text-base font-medium text-foreground">
            전체 진행률
          </span>
          <span className="text-sm md:text-base font-bold text-primary data-text">
            {challenge?.completedProofs}/{challenge?.totalProofs} 완료
          </span>
        </div>

        <div className="relative w-full h-4 md:h-5 bg-muted rounded-full overflow-hidden">
          <div
            className="absolute inset-y-0 left-0 bg-primary rounded-full transition-gaming"
            style={{
              width: `${progressPercentage}%`
            }}
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-xs font-bold text-foreground data-text">
              {progressPercentage?.toFixed(0)}%
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mt-4">
          <div className="flex items-center space-x-2 p-3 bg-success/10 rounded-md border border-success/20">
            <Icon name="CheckCircle" size={18} color="var(--color-success)" />
            <div className="flex-1 min-w-0">
              <div className="text-xs text-muted-foreground caption">완료</div>
              <div className="text-base md:text-lg font-bold text-foreground data-text">
                {challenge?.completedProofs}
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2 p-3 bg-warning/10 rounded-md border border-warning/20">
            <Icon name="Clock" size={18} color="var(--color-warning)" />
            <div className="flex-1 min-w-0">
              <div className="text-xs text-muted-foreground caption">대기중</div>
              <div className="text-base md:text-lg font-bold text-foreground data-text">
                {challenge?.totalProofs - challenge?.completedProofs}
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2 p-3 bg-primary/10 rounded-md border border-primary/20">
            <Icon name="Star" size={18} color="var(--color-primary)" />
            <div className="flex-1 min-w-0">
              <div className="text-xs text-muted-foreground caption">총 별점</div>
              <div className="text-base md:text-lg font-bold text-foreground data-text">
                {challenge?.totalStars}
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2 p-3 bg-primary/10 rounded-md border border-primary/20">
            <Icon name="TrendingUp" size={18} color="var(--color-primary)" />
            <div className="flex-1 min-w-0">
              <div className="text-xs text-muted-foreground caption">연속</div>
              <div className="text-base md:text-lg font-bold text-foreground data-text">
                {challenge?.currentStreak}일
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChallengeHeader;